<template>
  <!-- Custom select-like dropdown: unlike a native <select>, each item can show a muted second line
       (description) and the selected item gets a check. v-model holds the selected value. -->
  <b-dropdown class="wDropdown" :disabled="!enabled" :mobile-modal="false" expanded aria-role="list">
    <template #trigger="{ active }">
      <button type="button" class="wDropdown__trigger" :class="{'wDropdown__trigger--small': size === 'small'}">
        <span class="wDropdown__value">{{ selectedLabel }}</span>
        <w-icon class="wDropdown__chevron" :class="{'wDropdown__chevron--open': active}"
                icon="chevron-down" color="light2"/>
      </button>
    </template>

    <b-dropdown-item v-for="item in items" :key="item.value" class="wDropdown__item"
                     :class="{'wDropdown__item--disabled': item.disabled}"
                     :disabled="item.disabled"
                     aria-role="listitem" @click="select(item)">
      <w-tool-tip :position="item.tooltipPosition || 'top'" :expanded="true"
                  :label="item.tooltip || ''" :interactive="!!item.tooltipInteractive">
        <div class="wDropdown__itemRow">
          <div class="wDropdown__itemText">
            <div class="wDropdown__itemLabel">
              <span>{{ item.label }}</span>
              <w-icon v-if="item.icon" :icon="item.icon" :color="item.iconColor || ''"/>
            </div>
            <div v-if="item.description" class="wDropdown__itemDescription">{{ item.description }}</div>
          </div>
          <w-icon v-if="item.value === value" icon="check" color="primary" size="small"/>
        </div>
      </w-tool-tip>
    </b-dropdown-item>
  </b-dropdown>
</template>

<script lang="ts">
import {Component, Prop, Vue} from 'vue-property-decorator';
import WIcon from '@/components/wrapper/w-icon.vue';
import WToolTip from '@/components/wrapper/w-toolTip.vue';

export interface WDropdownItem {
  value: string;
  label: string;
  // Optional muted second line under the label (e.g. "Uses AI credits").
  description?: string;
  // When true, the item is rendered muted and ignores clicks.
  disabled?: boolean;
  // Optional MDI icon shown next to the label
  icon?: string;
  // Color name passed to w-icon when `icon` is set
  iconColor?: string;
  // Tooltip shown on hover.
  tooltip?: string;
  // When true the tooltip stays open while hovered, so a link inside it can be clicked.
  tooltipInteractive?: boolean;
  // Popper placement for the row tooltip (default 'top').
  tooltipPosition?: string;
}

@Component({components: {WIcon, WToolTip}})
export default class WDropdown extends Vue {
  // Selected value (v-model).
  @Prop({default: ''})
  private value!: string;

  @Prop({default: () => []})
  private items!: WDropdownItem[];

  // '' (default) or 'small' to line up with neighbouring small selects.
  @Prop({default: ''})
  private size!: string;

  @Prop({default: true})
  private enabled!: boolean;

  get selectedLabel(): string {
    const found = this.items.find((item) => item.value === this.value);
    return found ? found.label : '';
  }

  public select(item: WDropdownItem) {
    if (item.disabled) return;
    this.$emit('input', item.value);
  }
}
</script>

<style scoped lang="scss">
.wDropdown {
  &__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    min-height: 40px;
    padding: 0 16px;
    background: var(--white);
    border: 1px solid var(--light1);
    border-radius: 8px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    // Override the global `button { text-transform: uppercase }` so the value reads like the selects.
    text-transform: none;
    color: var(--black);
    cursor: pointer;
    transition: border-color 0.15s;

    &:hover {
      border-color: var(--light2);
    }

    &--small {
      min-height: 36px;
      padding: 0 12px;
    }
  }

  &__value {
    flex: 1;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__chevron {
    flex-shrink: 0;
    transition: transform 0.15s;

    &--open {
      transform: rotate(180deg);
    }
  }

  // Class is on the b-dropdown-item root, so we lay out label + check.
  &__item {
    &--disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  &__itemRow {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    white-space: normal;
  }

  &__itemLabel {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    color: var(--black);

    .icon.is-small {
      font-size: 16px;
    }
  }

  &__itemDescription {
    margin-top: 2px;
    font-size: 12px;
    font-weight: 400;
    color: var(--light2);
  }
}
</style>
